// Questions Lio couldn't answer, reported by the people who asked them.
//
// This is the only view of them. A gap in Lio is TeamLio's problem, not the
// reporting company's, so the queue spans every organisation and no customer
// admin has a window onto it — the backend has no per-org endpoint to call.
//
// Assignees come from the PRODUCT ORG, not the org that reported the issue:
// the people who close a Lio gap are the TeamLio team, who are themselves
// employees of a workspace in this system. Which workspace that is has to be
// nominated once (the picker at the top) because the superadmin account is a
// bare platform login with no employees of its own.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Building2,
  Check,
  ChevronDown,
  Filter,
  Inbox,
  Loader2,
  RefreshCw,
  Search,
  UserPlus,
  X,
} from "lucide-react";

// The console's own dropdown, not a native <select>: a browser draws a
// select's open list in its own blue-on-white chrome, which is the exact
// mismatch FilterDropdown was written to end on the Workspaces toolbar.
// Searchable everywhere here — both lists grow with the customer base.
import { FilterDropdown, type FilterOption } from "./FilterDropdown";

import {
  listLioIssues,
  setLioProductOrg,
  updateLioIssue,
  type LioAssignee,
  type LioIssue,
  type LioIssueStatus,
  type SuperadminAdmin,
} from "../../api/superadmin";

type StatusFilter = "open_all" | LioIssueStatus;

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "open_all", label: "Needs attention" },
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In progress" },
  { key: "resolved", label: "Resolved" },
  { key: "dismissed", label: "Dismissed" },
];

const STATUS_LABEL: Record<LioIssueStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  dismissed: "Dismissed",
};

const STATUS_CHIP: Record<LioIssueStatus, string> = {
  open: "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-indigo-50 text-indigo-700 border-indigo-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  dismissed: "bg-gray-100 text-gray-500 border-gray-200",
};

function whenLabel(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function LioIssueQueue({ admins }: { admins: SuperadminAdmin[] }) {
  const [status, setStatus] = useState<StatusFilter>("open_all");
  const [assignedFilter, setAssignedFilter] = useState<string>(""); // "" = anyone
  const [search, setSearch] = useState("");

  const [issues, setIssues] = useState<LioIssue[]>([]);
  const [counts, setCounts] = useState<Record<LioIssueStatus, number> | null>(null);
  const [unassigned, setUnassigned] = useState(0);
  const [assignees, setAssignees] = useState<LioAssignee[]>([]);
  const [productOrgId, setProductOrgId] = useState<string | null>(null);
  const [productOrgName, setProductOrgName] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingOrg, setSavingOrg] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listLioIssues({
        status,
        search: search.trim() || undefined,
        assigned_to: assignedFilter || undefined,
      });
      setIssues(data.issues);
      setCounts(data.counts);
      setUnassigned(data.unassigned_count);
      setAssignees(data.assignees);
      setProductOrgId(data.product_org_id);
      setProductOrgName(data.product_org_name);
      setError(null);
    } catch {
      setError("Couldn't load the reported questions. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [status, search, assignedFilter]);

  useEffect(() => {
    // Debounced so typing in the search box doesn't fire a request per
    // keystroke; a filter change still feels immediate at 300ms.
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  // Every workspace, as the product-org picker's options. headOfficeID is what
  // the issues are stamped with and what the employee lookup keys on; for a
  // level-1 owner it equals the admin's own user id, which is the fallback.
  const orgOptions = useMemo<FilterOption[]>(
    () => [
      { value: "", label: "— none chosen —" },
      ...admins
        .map((a) => ({
          value: a.headOfficeID || a.user_id,
          label: a.company_name || a.name || a.email,
        }))
        .sort((x, y) => x.label.localeCompare(y.label)),
    ],
    [admins],
  );

  // "Anyone" and "Unassigned" lead; the product org's people follow. Rebuilt
  // whenever the nominated org changes, which is when `assignees` changes.
  const assignedOptions = useMemo<FilterOption[]>(
    () => [
      { value: "", label: "Everyone's" },
      { value: "unassigned", label: "Nobody's (unassigned)" },
      ...assignees.map((p) => ({ value: p.user_id, label: `${p.name}'s` })),
    ],
    [assignees],
  );

  // The per-card picker: just the people. Multi-select, so "unassigned" is
  // simply nobody ticked — an explicit clear row would be a second way to say
  // the same thing.
  const assignOptions = useMemo<FilterOption[]>(
    () =>
      assignees.map((p) => ({
        value: p.user_id,
        label: p.designation ? `${p.name} · ${p.designation}` : p.name,
      })),
    [assignees],
  );

  const chooseOrg = useCallback(async (id: string) => {
    setSavingOrg(true);
    try {
      const res = await setLioProductOrg(id);
      setProductOrgId(res.product_org_id);
      setProductOrgName(res.product_org_name);
      setAssignees(res.assignees);
      setError(null);
    } catch {
      setError("Couldn't save the product team. Please try again.");
    } finally {
      setSavingOrg(false);
    }
  }, []);

  const applied = useCallback(
    (updated: LioIssue) => {
      setIssues((prev) =>
        // Drop the row when it no longer matches the active filter, so the
        // list never shows a "Resolved" card under "Needs attention".
        prev.flatMap((r) => {
          if (r.id !== updated.id) return [r];
          const stillMatches =
            status === "open_all"
              ? updated.status === "open" || updated.status === "in_progress"
              : updated.status === status;
          return stillMatches ? [updated] : [];
        }),
      );
      // The chips count everything, not the page — re-read them.
      listLioIssues({ status, search: search.trim() || undefined })
        .then((d) => {
          setCounts(d.counts);
          setUnassigned(d.unassigned_count);
        })
        .catch(() => {
          /* the list is already right; stale chips aren't worth an error */
        });
    },
    [status, search],
  );

  const needsAttention = counts ? counts.open + counts.in_progress : 0;

  return (
    <div className="h-full flex flex-col bg-white rounded-2xl border border-gray-100 shadow-sm">
      {/* Toolbar */}
      <div className="shrink-0 p-4 border-b border-gray-100 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Inbox className="w-4 h-4 text-emerald-600" />
            Reported questions
            {needsAttention > 0 && (
              <span className="text-xs font-medium text-gray-500">
                · {needsAttention} waiting
                {unassigned > 0 ? `, ${unassigned} unassigned` : ""}
              </span>
            )}
          </div>

          <div className="relative flex-1 min-w-0 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search questions"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-gray-200 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
          </div>

          {/* A FILTER, not an assign control. It read "Assigned to" at first,
              word for word the same as the one on each card, and the first
              person to use the page set it expecting the issue to be handed
              over. Named for what it does to the list instead. */}
          <FilterDropdown
            label="Show"
            value={assignedFilter}
            onChange={setAssignedFilter}
            options={assignedOptions}
            placeholder="Everyone's"
            widthClass="sm:w-52"
            searchable
            searchPlaceholder="Search people…"
            emptyText="Nobody by that name."
            icon={<Filter className="w-4 h-4 shrink-0 text-emerald-600" />}
          />

          <button
            onClick={load}
            disabled={loading}
            title="Refresh"
            className="ml-auto p-2 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Who fixes these. Sits in the toolbar rather than a settings screen
            because with no org nominated the queue cannot be assigned at all,
            and that is the first thing to deal with. */}
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <div className="w-56">
            <FilterDropdown
              label="Product team"
              value={productOrgId || ""}
              onChange={chooseOrg}
              options={orgOptions}
              placeholder="— none chosen —"
              widthClass="w-full"
              searchable
              searchPlaceholder="Search company…"
              emptyText="No matching company."
              icon={<Building2 className="w-4 h-4 shrink-0 text-emerald-600" />}
            />
          </div>
          {savingOrg && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
          {productOrgId && assignees.length > 0 && (
            <span className="text-gray-400">
              {assignees.length} {assignees.length === 1 ? "person" : "people"} can be
              assigned
            </span>
          )}
          {productOrgId && !savingOrg && assignees.length === 0 && (
            <span className="inline-flex items-center gap-1 text-amber-700">
              <AlertCircle className="w-3.5 h-3.5" />
              {productOrgName || "That workspace"} has no active employees — nobody to
              assign to.
            </span>
          )}
          {!productOrgId && !savingOrg && (
            <span className="inline-flex items-center gap-1 text-amber-700">
              <AlertCircle className="w-3.5 h-3.5" />
              Pick the workspace your team works in to assign these.
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {STATUS_FILTERS.map((f) => {
            const active = status === f.key;
            const n =
              counts == null
                ? null
                : f.key === "open_all"
                  ? counts.open + counts.in_progress
                  : counts[f.key];
            return (
              <button
                key={f.key}
                onClick={() => setStatus(f.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                  active
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                }`}
              >
                {f.label}
                {n != null && n > 0 ? ` (${n})` : ""}
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {error && (
          <p className="mb-3 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        {loading && issues.length === 0 ? (
          <div className="flex items-center gap-2 py-10 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        ) : issues.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-sm text-gray-500">
            <Inbox className="w-6 h-6 text-gray-300" />
            {status === "open_all"
              ? "Nothing waiting — Lio is answering what people ask."
              : "No reports here."}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {issues.map((issue) => (
              <IssueCard
                key={issue.id}
                issue={issue}
                assignees={assignees}
                assignOptions={assignOptions}
                onApplied={applied}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function IssueCard({
  issue,
  assignees,
  assignOptions,
  onApplied,
}: {
  issue: LioIssue;
  assignees: LioAssignee[];
  /** Built once in the parent — every card offers the same people. */
  assignOptions: FilterOption[];
  onApplied: (updated: LioIssue) => void;
}) {
  const [response, setResponse] = useState(issue.response || "");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The picker edits this; "Save people" sends it. Re-seeded whenever the
  // server's answer changes, so a save (or someone else's) is reflected.
  const serverPicked = useMemo(
    () => issue.assignees.map((a) => a.user_id),
    [issue.assignees],
  );
  const [picked, setPicked] = useState<string[]>(serverPicked);
  useEffect(() => setPicked(serverPicked), [serverPicked]);

  const dirty = response.trim() !== (issue.response || "").trim();
  const assigneesDirty =
    [...picked].sort().join(",") !== [...serverPicked].sort().join(",");

  const apply = useCallback(
    async (
      body: { status?: LioIssueStatus; assignees?: string[] },
      tag: string,
    ) => {
      setBusy(tag);
      setError(null);
      try {
        // Send the written answer alongside whatever else changed rather than
        // making the superadmin save twice — "resolved, and here's why" is one
        // action.
        const updated = await updateLioIssue(issue.id, {
          ...body,
          response: dirty ? response : undefined,
        });
        onApplied(updated);
      } catch (e: any) {
        setError(e?.response?.data?.detail || "Couldn't save that. Please try again.");
      } finally {
        setBusy(null);
      }
    },
    [issue.id, response, dirty, onApplied],
  );

  return (
    <div className="rounded-2xl border border-gray-200 p-4 flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span
          className={`px-2 py-0.5 rounded-full border font-medium ${STATUS_CHIP[issue.status]}`}
        >
          {STATUS_LABEL[issue.status]}
        </span>
        {issue.org_name && (
          <span className="inline-flex items-center gap-1 text-gray-600 font-medium">
            <Building2 className="w-3 h-3" />
            {issue.org_name}
          </span>
        )}
        <span className="text-gray-400">
          {issue.reporter_name}
          {issue.reporter_role ? ` · ${issue.reporter_role}` : ""}
          {issue.created_at ? ` · ${whenLabel(issue.created_at)}` : ""}
        </span>
        {issue.tool_used && (
          <span className="text-gray-400">· tried {issue.tool_used}</span>
        )}
      </div>

      <p className="text-sm text-gray-900">{issue.question}</p>

      {issue.note && (
        <p className="text-xs text-gray-600">
          <span className="font-semibold">They added:</span> {issue.note}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3 pt-1">
        <div className="w-60">
          <FilterDropdown
            label="Assigned to"
            value=""
            onChange={() => {}}
            multiple
            values={picked}
            // Saved on close, not per tick: a Lio gap often needs two people,
            // and a PATCH per checkbox would fire a bell at the first one
            // before the set is even decided.
            onChangeMultiple={setPicked}
            options={assignOptions}
            placeholder={
              assignees.length === 0 ? "No product team chosen" : "Unassigned"
            }
            widthClass="w-full"
            searchable
            searchPlaceholder="Search people…"
            emptyText="Nobody by that name."
            icon={<UserPlus className="w-4 h-4 shrink-0 text-emerald-600" />}
            // Centred, not hanging off the trigger: this row lives inside a
            // scrolling list of cards, which clipped the anchored panel.
            centered
          />
        </div>
        {assigneesDirty && (
          <Action
            label="Save people"
            primary
            busy={busy === "assign"}
            onClick={() => apply({ assignees: picked }, "assign")}
          />
        )}
        {busy === "assign" ? (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Assigning…
          </span>
        ) : (
          issue.assignees.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
              <Check className="w-3.5 h-3.5" />
              With {issue.assignees.map((a) => a.name).join(", ")}
            </span>
          )
        )}
      </div>

      <textarea
        value={response}
        onChange={(e) => setResponse(e.target.value)}
        maxLength={2000}
        rows={2}
        placeholder="Answer the person who asked (optional)…"
        className="w-full resize-none px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-200"
      />

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex flex-wrap items-center gap-2">
        {issue.status !== "in_progress" && issue.status !== "resolved" && (
          <Action
            label="Working on it"
            busy={busy === "in_progress"}
            onClick={() => apply({ status: "in_progress" }, "in_progress")}
          />
        )}
        {issue.status !== "resolved" && (
          <Action
            label="Resolve"
            icon={<Check className="w-3.5 h-3.5" />}
            primary
            busy={busy === "resolved"}
            onClick={() => apply({ status: "resolved" }, "resolved")}
          />
        )}
        {issue.status !== "dismissed" && (
          <Action
            label="Dismiss"
            icon={<X className="w-3.5 h-3.5" />}
            busy={busy === "dismissed"}
            onClick={() => apply({ status: "dismissed" }, "dismissed")}
          />
        )}
        {dirty && (
          <Action
            label="Save reply"
            busy={busy === "response"}
            onClick={() => apply({}, "response")}
          />
        )}
        {issue.reviewed_by_name && (
          <span className="ml-auto text-[11px] text-gray-400">
            Last touched by {issue.reviewed_by_name}
            {issue.reviewed_at ? ` · ${whenLabel(issue.reviewed_at)}` : ""}
          </span>
        )}
      </div>

      {issue.reply && <LioReply text={issue.reply} />}
    </div>
  );
}

/** What Lio actually said, collapsed by default.
 *
 *  It is the least useful thing on the card and by far the longest — a reply
 *  can be a full Markdown table of every overdue task — so left open it buried
 *  the assign control and the status buttons below the fold. Useful when you
 *  want it, out of the way when you don't. */
function LioReply({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="pt-1 border-t border-gray-100">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600"
      >
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
        />
        {open ? "Hide what Lio said" : "See what Lio said"}
      </button>
      {open && (
        // Pre-wrapped and scrollable: the reply is Markdown, often a wide
        // table, and letting it reflow into prose makes it unreadable.
        <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-500 font-sans">
          {text}
        </pre>
      )}
    </div>
  );
}

function Action({
  label,
  icon,
  primary,
  busy,
  onClick,
}: {
  label: string;
  icon?: React.ReactNode;
  primary?: boolean;
  busy: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-xl border transition-colors disabled:opacity-40 ${
        primary
          ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
          : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
      }`}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : icon}
      {label}
    </button>
  );
}
